// src/components/backend/companies/CompaniesDialog.jsx
import React from "react";
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Switch,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import { CUSTNO_RE } from "./utils/companyUtils";
import useCompanyForm from "../../hooks/useComaniesForm";
import useImageSelection from "../../hooks/useImageSelection";

import SectionStammdaten from "./sections/SectionStammdaten";
import SectionKontakt from "./sections/SectionKontakt";
import SectionHotel from "./sections/SectionHotel";
import SectionKategorien from "./sections/SectionKategorien";
import SectionAusstattungen from "./sections/SectionAusstattungen";
import SectionHotelgruppen from "./sections/SectionHotelgruppen";
import SectionAuszeichnungen from "./sections/SectionAuszeichnungen";
import SectionLogos from "./sections/SectionLogos";
import SectionKuechen from "./sections/SectionKuechen";
import SectionPreise from "./sections/SectionPreise";
import SectionLieferservice from "./sections/SectionLieferservice";
import SectionStandorte from "./sections/SectionStandorte";
import SectionFreizeitSport from "./sections/SectionFreizeitSport";
import SectionRegionen from "./sections/SectionRegionen";
import SectionKlassifizierungen from "./sections/SectionKlassifizierungen";
import SectionZimmer from "./sections/SectionZimmer";
import SectionSeo from "./sections/SectionSeo";
import SectionRechnung from "./sections/SectionRechnung";
import SectionBilder from "./sections/SectionBilder";
import SectionSpeisekarten from "./sections/SectionSpeisekarten";
import { openCompanyScreenshotPrint } from "./utils/companyPrintUtils";

export default function CompaniesDialog({
                                            open,
                                            mode, // "create" | "edit"
                                            initialCompany,
                                            categories,
                                            categoriesLoading,
                                            categoriesError,
                                            amenities,
                                            amenitiesLoading,
                                            amenitiesError,
                                            hotelGroups,
                                            hotelGroupsLoading,
                                            hotelGroupsError,
                                            awards,
                                            awardsLoading,
                                            awardsError,
                                            logos,
                                            logosLoading,
                                            logosError,
                                            cuisines,
                                            cuisinesLoading,
                                            cuisinesError,
                                            restaurantPrices,
                                            restaurantPricesLoading,
                                            restaurantPricesError,
                                            deliveryServices,
                                            deliveryServicesLoading,
                                            deliveryServicesError,
                                            locations,
                                            locationsLoading,
                                            locationsError,
                                            leisureActivities,
                                            leisureActivitiesLoading,
                                            leisureActivitiesError,
                                            regions,
                                            regionsLoading,
                                            regionsError,
                                            classifications,
                                            classificationsLoading,
                                            classificationsError,
                                            editLockToken,
                                            onClose,
                                            api,
                                        }) {
    const isEdit = mode === "edit";

    const { form, setForm, blankForm, applyCompanyToForm, validateBase, buildPayloadBase } = useCompanyForm();
    const imageSelection = useImageSelection();

    // Only first open, others collapsed
    const [expanded, setExpanded] = React.useState("stammdaten");

    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");
    const [detailsLoadError, setDetailsLoadError] = React.useState("");
    const [editLockLost, setEditLockLost] = React.useState(false);

    const [dialogCompany, setDialogCompany] = React.useState(null);
    const [imagesLoading, setImagesLoading] = React.useState(false);

    const [sortingImages, setSortingImages] = React.useState(false);
    const [sortImagesError, setSortImagesError] = React.useState("");
    const [menusLoading, setMenusLoading] = React.useState(false);
    const [menusError, setMenusError] = React.useState("");

    const currentCompany = dialogCompany || initialCompany;
    const currentCompanyId = currentCompany?.id || null;
    const hasPersistedCompany = Boolean(currentCompanyId);
    const isDetailsLoading = Boolean(
        open && isEdit && initialCompany?.id && dialogCompany?.id !== initialCompany.id && !detailsLoadError
    );
    const accordionSlotProps = React.useMemo(
        () => ({ transition: { unmountOnExit: true } }),
        []
    );

    const handleAccordionChange = (panel) => (_e, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    React.useEffect(() => {
        if (!open) return;

        // Comments in English: Reset state on every open.
        setExpanded("stammdaten");
        setSubmitError("");
        setDetailsLoadError("");
        setEditLockLost(false);
        setSortImagesError("");
        setMenusError("");
        setDialogCompany(null);
        imageSelection.reset();

        if (!isEdit) {
            setForm(blankForm());
            return;
        }

        // Comments in English: Quick fill from list row.
        if (initialCompany) applyCompanyToForm(initialCompany);

        // Comments in English: Load full company for edit dialog.
        let cancelled = false;
        (async () => {
            setImagesLoading(true);
            try {
                const full = await api.getCompanyById(initialCompany.id);
                if (cancelled) return;
                setDialogCompany(full);
                applyCompanyToForm(full);
            } catch (e) {
                if (!cancelled) {
                    setDetailsLoadError(e?.message || "Details laden fehlgeschlagen.");
                }
            } finally {
                if (!cancelled) setImagesLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    React.useEffect(() => {
        if (!open || !isEdit || !initialCompany?.id || !editLockToken || !api?.refreshCompanyEditLock) return undefined;

        let cancelled = false;
        const refreshLock = async () => {
            try {
                await api.refreshCompanyEditLock(initialCompany.id, editLockToken);
            } catch (e) {
                if (cancelled) return;
                const msg =
                    e?.response?.data?.message ||
                    e?.response?.data?.error ||
                    "Die Bearbeitungssperre ist nicht mehr aktiv. Bitte schließe den Dialog und öffne die Firma erneut.";
                setEditLockLost(true);
                setSubmitError(msg);
            }
        };

        const timer = window.setInterval(refreshLock, 60000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [open, isEdit, initialCompany?.id, editLockToken, api]);

    const handleSave = async (closeAfterSave = false) => {
        setSubmitError("");

        if (editLockLost) {
            setSubmitError("Die Bearbeitungssperre ist nicht mehr aktiv. Bitte schließe den Dialog und öffne die Firma erneut.");
            return;
        }

        const err = validateBase(form, CUSTNO_RE);
        if (err) {
            setSubmitError(err);
            return;
        }

        setSubmitting(true);
        try {
            const payload = buildPayloadBase(form);

            const filesPayload = {
                logo: imageSelection.logoFile,
                removeLogo: imageSelection.removeLogo,
                startImage: imageSelection.startImageFile || null,
                startImageCrop: imageSelection.startImageFile ? imageSelection.startImageCrop : null,
                images: imageSelection.extraImageFiles.map((x) => x.file),
            };

            const savedCompany = hasPersistedCompany
                ? await api.updateCompany(currentCompanyId, payload, filesPayload, editLockToken)
                : await api.createCompany(payload, filesPayload);

            if (savedCompany) {
                setDialogCompany(savedCompany);
                applyCompanyToForm(savedCompany);
            }
            imageSelection.reset();

            if (closeAfterSave) onClose();
        } catch (e) {
            const msg = e?.response?.status === 413
                ? "Die hochgeladenen Dateien sind zu groß. Bitte weniger oder kleinere Bilder auswählen."
                : e?.response?.data?.error ||
                e?.response?.data?.message ||
                e?.message ||
                "Speichern fehlgeschlagen.";
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteImage = async (imageId) => {
        if (!currentCompany?.id) return;
        try {
            await api.deleteCompanyImage(currentCompany.id, imageId, editLockToken);
            const full = await api.getCompanyById(currentCompany.id);
            setDialogCompany(full);
            applyCompanyToForm(full);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert(e?.message || "Bild löschen fehlgeschlagen.");
        }
    };

    const handleSetStartImageFromExisting = async (imageId, crop = null) => {
        if (!currentCompany?.id) return;
        try {
            await api.setCompanyStartImage(currentCompany.id, imageId, crop, editLockToken);
            const full = await api.getCompanyById(currentCompany.id);
            setDialogCompany(full);
            applyCompanyToForm(full);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert(e?.message || "Startbild setzen fehlgeschlagen.");
        }
    };

    const handleSaveImageCrop = async (imageId, crop) => {
        if (!currentCompany?.id) return;
        try {
            const full = await api.updateCompanyImageCrop(currentCompany.id, imageId, crop, editLockToken);
            setDialogCompany(full);
            applyCompanyToForm(full);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert(e?.message || "Bild-Ausschnitt speichern fehlgeschlagen.");
        }
    };

    const handleMoveImageOrderedIds = async (orderedIds) => {
        if (!currentCompany?.id) return;

        setSortImagesError("");
        setSortingImages(true);
        try {
            const updated = await api.sortCompanyImages(currentCompany.id, orderedIds, editLockToken);
            setDialogCompany(updated);
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Sortierung speichern fehlgeschlagen.";
            setSortImagesError(msg);
            try {
                const full = await api.getCompanyById(currentCompany.id);
                setDialogCompany(full);
                applyCompanyToForm(full);
            } catch {
                // ignore
            }
        } finally {
            setSortingImages(false);
        }
    };

    const handleUploadMenu = async (menuType, file) => {
        if (!currentCompany?.id || !file) return;

        setMenusError("");
        setMenusLoading(true);
        try {
            const updated = await api.uploadCompanyMenu(currentCompany.id, menuType, file, editLockToken);
            setDialogCompany(updated);
            applyCompanyToForm(updated);
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Karten-Upload fehlgeschlagen.";
            setMenusError(msg);
        } finally {
            setMenusLoading(false);
        }
    };

    const handleDeleteMenu = async (menuType) => {
        if (!currentCompany?.id) return;

        setMenusError("");
        setMenusLoading(true);
        try {
            const updated = await api.deleteCompanyMenu(currentCompany.id, menuType, editLockToken);
            setDialogCompany(updated);
            applyCompanyToForm(updated);
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Karte löschen fehlgeschlagen.";
            setMenusError(msg);
        } finally {
            setMenusLoading(false);
        }
    };

    const handleSaveMenuUrl = async (menuType, externalUrl) => {
        if (!currentCompany?.id) return;

        setMenusError("");
        setMenusLoading(true);
        try {
            const updated = await api.setCompanyMenuUrl(currentCompany.id, menuType, externalUrl, editLockToken);
            setDialogCompany(updated);
            applyCompanyToForm(updated);
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Karten-Link speichern fehlgeschlagen.";
            setMenusError(msg);
        } finally {
            setMenusLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !submitting && !isDetailsLoading && !sortingImages && !menusLoading && onClose()}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { maxWidth: 1400 } }}
        >
            <DialogTitle>{isEdit || hasPersistedCompany ? "Firma bearbeiten" : "Neue Firma anlegen"}</DialogTitle>

            <Backdrop
                open={submitting || isDetailsLoading}
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <CircularProgress color="inherit" />
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "rgba(0, 0, 0, 0.45)",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="subtitle2">
                        {isDetailsLoading ? "Firmendaten werden geladen" : "Firmeneintrag wird gespeichert"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Bitte einen Moment warten.
                    </Typography>
                </Box>
            </Backdrop>

            <DialogContent dividers>
                {detailsLoadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {detailsLoadError}
                    </Alert>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle2">Status</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!form.is_active}
                                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                            />
                        }
                        label={form.is_active ? "Aktiv" : "Inaktiv"}
                    />
                </Box>

                <Accordion expanded={expanded === "stammdaten"} onChange={handleAccordionChange("stammdaten")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Stammdaten</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionStammdaten form={form} setForm={setForm} />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "kontakt"} onChange={handleAccordionChange("kontakt")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Adresse & Kontakt</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionKontakt form={form} setForm={setForm} />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "hotel"} onChange={handleAccordionChange("hotel")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Zusätzliche Hotelinformationen</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionHotel form={form} setForm={setForm} />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "kategorien"} onChange={handleAccordionChange("kategorien")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Kategorien {form.category_ids?.length ? `(${form.category_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionKategorien
                            form={form}
                            setForm={setForm}
                            categories={categories}
                            categoriesLoading={categoriesLoading}
                            categoriesError={categoriesError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "ausstattungen"} onChange={handleAccordionChange("ausstattungen")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Ausstattungen {form.amenity_ids?.length ? `(${form.amenity_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionAusstattungen
                            form={form}
                            setForm={setForm}
                            amenities={amenities}
                            amenitiesLoading={amenitiesLoading}
                            amenitiesError={amenitiesError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "hotelgruppen"} onChange={handleAccordionChange("hotelgruppen")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Hotelgruppen {form.hotel_group_ids?.length ? `(${form.hotel_group_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionHotelgruppen
                            form={form}
                            setForm={setForm}
                            hotelGroups={hotelGroups}
                            hotelGroupsLoading={hotelGroupsLoading}
                            hotelGroupsError={hotelGroupsError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "auszeichnungen"} onChange={handleAccordionChange("auszeichnungen")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Auszeichnungen {form.award_ids?.length ? `(${form.award_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionAuszeichnungen
                            form={form}
                            setForm={setForm}
                            awards={awards}
                            awardsLoading={awardsLoading}
                            awardsError={awardsError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "logos"} onChange={handleAccordionChange("logos")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Logos {form.logo_ids?.length ? `(${form.logo_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionLogos
                            form={form}
                            setForm={setForm}
                            logos={logos}
                            logosLoading={logosLoading}
                            logosError={logosError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "kuechen"} onChange={handleAccordionChange("kuechen")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Küche {form.cuisine_ids?.length ? `(${form.cuisine_ids.length}/5 ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionKuechen
                            form={form}
                            setForm={setForm}
                            cuisines={cuisines}
                            cuisinesLoading={cuisinesLoading}
                            cuisinesError={cuisinesError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "preise"} onChange={handleAccordionChange("preise")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Preise {form.restaurant_price_ids?.length ? `(${form.restaurant_price_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionPreise
                            form={form}
                            setForm={setForm}
                            restaurantPrices={restaurantPrices}
                            restaurantPricesLoading={restaurantPricesLoading}
                            restaurantPricesError={restaurantPricesError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "lieferservice"} onChange={handleAccordionChange("lieferservice")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Lieferservice {form.delivery_service_ids?.length ? `(${form.delivery_service_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionLieferservice
                            form={form}
                            setForm={setForm}
                            deliveryServices={deliveryServices}
                            deliveryServicesLoading={deliveryServicesLoading}
                            deliveryServicesError={deliveryServicesError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "standorte"} onChange={handleAccordionChange("standorte")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Standorte / Umgebung {form.location_ids?.length ? `(${form.location_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionStandorte
                            form={form}
                            setForm={setForm}
                            locations={locations}
                            locationsLoading={locationsLoading}
                            locationsError={locationsError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "freizeitSport"} onChange={handleAccordionChange("freizeitSport")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Freizeit & Sport {form.leisure_activity_ids?.length ? `(${form.leisure_activity_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionFreizeitSport
                            form={form}
                            setForm={setForm}
                            leisureActivities={leisureActivities}
                            leisureActivitiesLoading={leisureActivitiesLoading}
                            leisureActivitiesError={leisureActivitiesError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "regionen"} onChange={handleAccordionChange("regionen")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Regionen {form.region_ids?.length ? `(${form.region_ids.length} ausgewählt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionRegionen
                            form={form}
                            setForm={setForm}
                            regions={regions}
                            regionsLoading={regionsLoading}
                            regionsError={regionsError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "klassifizierungen"} onChange={handleAccordionChange("klassifizierungen")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Klassifizierungen {form.classification_assignments?.length ? `(${form.classification_assignments.length} zugewiesen)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionKlassifizierungen
                            form={form}
                            setForm={setForm}
                            classifications={classifications}
                            classificationsLoading={classificationsLoading}
                            classificationsError={classificationsError}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "zimmer"} onChange={handleAccordionChange("zimmer")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Zimmer {form.room_details?.length ? `(${form.room_details.length} Merkmale)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionZimmer form={form} setForm={setForm} />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "seo"} onChange={handleAccordionChange("seo")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Suchmaschinen Optimierung</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionSeo form={form} setForm={setForm} />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "rechnung"} onChange={handleAccordionChange("rechnung")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Rechnungsinformationen</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionRechnung form={form} setForm={setForm} />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "bilder"} onChange={handleAccordionChange("bilder")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Bilder</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {imagesLoading ? (
                            <Box sx={{ py: 2, textAlign: "center" }}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : (
                            <SectionBilder
                                company={currentCompany}
                                imageSelection={imageSelection}
                                submitting={submitting}
                                sortingImages={sortingImages}
                                sortImagesError={sortImagesError}
                                onDeleteImage={handleDeleteImage}
                                onSetStartImage={handleSetStartImageFromExisting}
                                onSaveImageCrop={handleSaveImageCrop}
                                onMoveOrderedIds={handleMoveImageOrderedIds}
                            />
                        )}
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === "speisekarten"} onChange={handleAccordionChange("speisekarten")} slotProps={accordionSlotProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            Speisekarten {currentCompany?.menus?.length ? `(${currentCompany.menus.length} hinterlegt)` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SectionSpeisekarten
                            company={currentCompany}
                            submitting={submitting}
                            menusLoading={menusLoading}
                            menusError={menusError}
                            onUploadMenu={handleUploadMenu}
                            onDeleteMenu={handleDeleteMenu}
                            onSaveMenuUrl={handleSaveMenuUrl}
                        />
                    </AccordionDetails>
                </Accordion>

                {submitError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {submitError}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={submitting || isDetailsLoading || sortingImages || menusLoading}>
                    Abbrechen
                </Button>

                <Button
                    variant="outlined"
                    onClick={() => window.open(`/companies/${currentCompany.slug || currentCompany.id}`, "_blank")}
                    disabled={!currentCompany?.id || isDetailsLoading || menusLoading}
                >
                    Live Ansicht
                </Button>

                <Button
                    variant="outlined"
                    onClick={() => openCompanyScreenshotPrint(currentCompany)}
                    disabled={!currentCompany?.id || isDetailsLoading || menusLoading}
                >
                    Live Ansicht drucken
                </Button>

                <Button
                    variant="contained"
                    onClick={() => handleSave(false)}
                    disabled={submitting || isDetailsLoading || editLockLost || sortingImages || menusLoading}
                >
                    {submitting ? "Speichern…" : "Speichern"}
                </Button>

                <Button
                    variant="contained"
                    onClick={() => handleSave(true)}
                    disabled={submitting || isDetailsLoading || editLockLost || sortingImages || menusLoading}
                >
                    {submitting ? "Speichern…" : "Speichern und schließen"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
